/**
 * @abstract
 * Represents this user's active presence slot on a quote.
 * Concrete implementations hold provider-specific connection state privately.
 */
export class PresenceSession {
  /** @returns {string} */
  get sessionId() { throw new Error('Not implemented') }

  /**
   * Partially update this user's presence profile (e.g. isEditing, activeTab).
   * @param {Object} updates
   * @returns {Promise<void>}
   */
  async update(updates) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Remove this user's presence record (called on clean unmount).
   * @returns {Promise<void>}
   */
  async leave() { throw new Error('Not implemented') }
}

/**
 * @abstract
 * Transport-agnostic presence service.
 * Swap the concrete implementation in src/services/presence/index.js without
 * touching any component or view.
 */
export class PresenceProvider {
  /**
   * Authenticate and write this user's presence record for a quote.
   * @param {string} quoteId
   * @param {string} companyId
   * @param {Object} profile  { userId, firstName, lastName, displayName, isEditing, activeTab }
   * @returns {Promise<PresenceSession|null>}  null when presence is disabled for this company
   */
  async enter(quoteId, companyId, profile) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Subscribe to presence changes for a quote.
   * @param {string} quoteId
   * @param {function(Array): void} callback  receives array of member objects
   * @returns {() => void}  unsubscribe
   */
  subscribeToMembers(quoteId, callback) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Subscribe to server-side quote save events.
   * Fires callback only for saves that occur AFTER the subscription is established.
   * @param {string} quoteId
   * @param {function(Object): void} callback  receives { lastSavedAt, savedBy, savedByName, source }
   * @returns {() => void}  unsubscribe
   */
  subscribeToQuoteSaves(quoteId, callback) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Subscribe to real-time quote entity updates written by the server after each save.
   * Fires callback only for saves that occur AFTER the subscription is established.
   * @param {string} quoteId
   * @param {function(Object): void} callback  receives { ...quoteEntity, savedBy, savedByName, lastSavedAt }
   * @returns {() => void}  unsubscribe
   */
  subscribeToQuoteEntity(quoteId, callback) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Subscribe to quote deletion events.
   * Fires callback only for deletions that occur AFTER the subscription is established.
   * @param {string} quoteId
   * @param {function(Object): void} callback  receives { deletedAt, deletedBy, deletedBySession, deletedByName }
   * @returns {() => void}  unsubscribe
   */
  subscribeToQuoteDeleted(quoteId, callback) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Subscribe to server-side customer save events.
   * Fires callback only for updates that occur AFTER the subscription is established.
   * @param {string} customerId
   * @param {function(Object): void} callback  receives { updatedAt, updatedBy, updatedByName, source }
   * @returns {() => void}  unsubscribe
   */
  subscribeToCustomerUpdates(customerId, callback) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars

  /**
   * Subscribe to server-side vehicle save events.
   * Fires callback only for updates that occur AFTER the subscription is established.
   * @param {string} vehicleId
   * @param {function(Object): void} callback  receives { updatedAt, updatedBy, updatedByName, rego, source }
   * @returns {() => void}  unsubscribe
   */
  subscribeToVehicleUpdates(vehicleId, callback) { throw new Error('Not implemented') }  // eslint-disable-line no-unused-vars
}
